import {
    Button,
    Card,
    DatePicker,
    Input,
    Modal,
    notification,
    Skeleton,
    Form,
    Popconfirm,
    theme,
} from "antd";
import { useEffect, useState } from "react";
import HTTP from "../../services/httpService";
import CustomHeader from "../common/Header";
import {
    DeleteOutlined,
    EditOutlined,
    QuestionCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "./List.css";
import { formatDate } from "../../services/util";
import CustomTable from "../common/Table";
dayjs.extend(customParseFormat);

function UpcomingHotelList() {
    const token = theme.useToken();
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const emptyModel = {
        name: "",
        street: "",
        city: "",
        state: "",
        zip_code: "",
        to_live_date: "",
        hotel_id: "",
    };
    const [upcomingHotelData, setUpcomingHotelData] = useState([]);
    const [visible, setVisible] = useState(false);
    const [editUpcomingData, setEditUpcomingData] = useState(emptyModel);
    const [totalRecords, setTotalRecords] = useState(0);
    const [title, setTitle] = useState("Edit");
    const columns = [
        {
            title: "Id",
            dataIndex: "id",
            key: "id",
            sorter: () => {},
            width: 100,
        },
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            sorter: () => {},
        },
        {
            title: "Street",
            dataIndex: "street",
            key: "street",
            sorter: () => {},
        },
        {
            title: "City",
            dataIndex: "city",
            key: "city",
            sorter: () => {},
        },
        {
            title: "State",
            dataIndex: "state",
            key: "state",
            sorter: () => {},
        },
        {
            title: "Zipcode",
            dataIndex: "zip_code",
            key: "zip_code",
            sorter: () => {},
        },
        {
            title: "Action",
            dataIndex: "is_live",
            key: "is_live",
            fixed: "right",
            width: 75,
            render: (text, record) => (
                <>
                    <Popconfirm
                        title="Are you sure want to delete?"
                        onConfirm={() => deleteUpcoming(record.hotel_id)}
                        icon={
                            <QuestionCircleOutlined
                                style={{
                                    color: token.colorError,
                                }}
                            />
                        }
                    >
                        <DeleteOutlined style={{ color: token.colorError }} />
                    </Popconfirm>
                    <EditOutlined
                        style={{ color: token.colorPrimary }}
                        id={record.hotel_id}
                        onClick={editUpcoming}
                    />
                </>
            ),
        },
    ];

    useEffect(() => {
        async function fatchData() {
            const { data: upcomingHotelData, total } = await HTTP(
                "/dashboard/getUpcomingHotels",
                {
                    staff_id,
                    page: 0,
                    limit: 10,
                },
            );
            setTotalRecords(total);
            setUpcomingHotelData(upcomingHotelData);
        }
        fatchData();
    }, []);

    async function deleteUpcoming(hotel_id) {
        const formData = {
            staff_id,
            hotel_id,
        };
        const {
            data: deleteResponse,
            success,
            message,
        } = await HTTP("/dashboard/deleteUpcomingHotel", formData);
        if (success === 0) {
            return notification["error"]({
                message: message,
            });
        } else {
            notification["info"]({
                message: "Delete action performed successfully.",
            });
            const [deleted] = deleteResponse;
            const updatedUpcomingHotelData = upcomingHotelData.filter((e) => {
                return e.id !== deleted.id;
            });
            setTotalRecords(totalRecords - 1);
            setUpcomingHotelData(updatedUpcomingHotelData);
        }
    }

    async function editUpcoming(e) {
        setTitle("Edit");
        const formData = {
            staff_id,
            hotel_id: e.currentTarget.getAttribute("id"),
        };
        const { data: editHotelData } = await HTTP(
            "/dashboard/getUpcomingHotelDetail",
            formData,
        );
        if (editHotelData?.success === 0) {
            return notification["error"]({
                message: editHotelData.message,
            });
        } else {
            setEditUpcomingData(editHotelData[0]);
            setVisible(true);
        }
    }

    function openAddUpcomingModel() {
        setTitle("Add");
        setEditUpcomingData(emptyModel);
        setVisible(true);
    }

    function closeModel() {
        setVisible(false);
        setEditUpcomingData(emptyModel);
    }

    async function handleSubmit(formData) {
        formData.staff_id = staff_id;
        formData.to_live_date = formatDate(formData.to_live_date);
        setVisible(false);
        setEditUpcomingData(emptyModel);
        if (formData.hotel_id !== "") {
            submitFormToUpdate(formData);
        } else {
            delete formData.hotel_id;
            submitFormToAdd(formData);
        }
    }

    async function submitFormToAdd(formData) {
        const {
            data: addUpcomingHotels,
            success,
            message,
        } = await HTTP("/dashboard/addUpcomingHotel", formData);
        if (success === 0) {
            return notification["error"]({
                message: message,
            });
        } else {
            //set upcomingHotelData from response
            notification["info"]({
                message: "Add action performed successfully.",
            });
            const [added] = addUpcomingHotels;
            const currentHotels = [...upcomingHotelData];
            currentHotels.splice(0, 0, added);
            setTotalRecords(currentHotels.length + 1);
            setUpcomingHotelData(currentHotels);
        }
    }

    async function submitFormToUpdate(formData) {
        const {
            data: updatedUpcomingHotels,
            success,
            message,
        } = await HTTP("/dashboard/editUpcomingHotel", formData);

        if (success === 0) {
            return notification["error"]({
                message: message,
            });
        } else {
            //set upcomingHotelData from response
            notification["info"]({
                message: "Update action performed successfully.",
            });
            const [updated] = updatedUpcomingHotels;
            const updatedUpcomingHotelData = upcomingHotelData.filter(
                (e) => e.id !== updated.id,
            );
            updatedUpcomingHotelData.splice(0, 0, updated);
            setUpcomingHotelData(updatedUpcomingHotelData);
        }
    }

    function disablePastDates(current) {
        return current && current < dayjs().endOf("day");
    }

    async function handleTableChange(pagination, _, sorter) {
        const { current, pageSize } = pagination;
        const { order, field } = sorter;
        const others = {
            page: current - 1,
            limit: pageSize,
        };
        if (order !== undefined) {
            others.sorting = order === "ascend" ? 1 : 2;
            others.sort_by = field;
        }
        const formData = {
            staff_id,
            ...others,
        };

        const { data: upcomingHotelData, total } = await HTTP(
            "/dashboard/getUpcomingHotels",
            formData,
        );
        setTotalRecords(total);
        setUpcomingHotelData(upcomingHotelData);
    }

    return (
        <>
            <Card className="dashboard-container">
                <CustomHeader callDataAPI={openAddUpcomingModel} />
                <Card className="box-shadow margin-10">
                    {upcomingHotelData.length === 0 ? (
                        <Skeleton
                            active
                            title={false}
                            paragraph={{ rows: 20, width: 40 }}
                        />
                    ) : (
                        <CustomTable
                            columns={columns}
                            tableData={upcomingHotelData}
                            noOfrecords={totalRecords}
                            handleTableChange={handleTableChange}
                            rowKey="hotel_id"
                            className="dashboard-table"
                            isScroll
                        />
                    )}
                </Card>
            </Card>
            <Modal
                title={title}
                open={visible}
                footer=""
                onCancel={closeModel}
                destroyOnHidden
            >
                <Form
                    name="basic"
                    labelCol={{
                        span: 8,
                    }}
                    wrapperCol={{
                        span: 16,
                    }}
                    onFinish={handleSubmit}
                    initialValues={{
                        name: editUpcomingData.name,
                        street: editUpcomingData.street,
                        city: editUpcomingData.city,
                        state: editUpcomingData.state,
                        zip_code: editUpcomingData.zip_code,
                        to_live_date:
                            editUpcomingData.to_live_date === "" ||
                            editUpcomingData.to_live_date === null
                                ? null
                                : dayjs(editUpcomingData.to_live_date),
                        hotel_id: editUpcomingData.hotel_id,
                    }}
                    autoComplete="off"
                >
                    <Form.Item
                        label="Hotel Name"
                        name="name"
                        rules={[
                            {
                                required: true,
                                message: "Please input hotel name!",
                            },
                        ]}
                        // initialValues={editUpcomingData}
                    >
                        <Input
                            placeholder="Hotel name"
                            id="name"
                            className="upcoming-form-inputs"
                        ></Input>
                    </Form.Item>

                    <Form.Item
                        label="Street"
                        name="street"
                        rules={[
                            {
                                required: true,
                                message: "Please enter street!",
                            },
                        ]}
                    >
                        <Input
                            placeholder="Street"
                            id="street"
                            className="upcoming-form-inputs"
                        ></Input>
                    </Form.Item>
                    <Form.Item
                        label="City"
                        name="city"
                        rules={[
                            {
                                required: true,
                                message: "Please enter city!",
                            },
                        ]}
                    >
                        <Input
                            placeholder="City"
                            id="city"
                            className="upcoming-form-inputs"
                        ></Input>
                    </Form.Item>

                    <Form.Item
                        label="State"
                        name="state"
                        rules={[
                            {
                                required: true,
                                message: "Please enter state!",
                            },
                        ]}
                    >
                        <Input
                            placeholder="State"
                            id="state"
                            className="upcoming-form-inputs"
                        ></Input>
                    </Form.Item>
                    <Form.Item
                        label="Zipcode"
                        name="zip_code"
                        rules={[
                            {
                                required: true,
                                message: "Please enter zipcode!",
                            },
                            {
                                pattern: /^(?:\d*)$/,
                                message: "Value should contain just number",
                            },
                            /* {
								pattern: /^[\d]{0,6}$/,
								message: "Value should be less than 6 character",
							}, */
                        ]}
                    >
                        <Input
                            type="text"
                            pattern="\d*"
                            maxLength="5"
                            placeholder="Zipcode"
                            id="zip_code"
                            className="upcoming-form-inputs"
                        ></Input>
                    </Form.Item>
                    <Form.Item
                        label="Date"
                        name="to_live_date"
                        rules={[
                            {
                                required: true,
                                message: "Please select date!",
                            },
                        ]}
                    >
                        <DatePicker
                            allowClear={false}
                            format={"YYYY-MM-DD"}
                            placeholder="Onboarding Date"
                            disabledDate={disablePastDates}
                            id="to_live_date"
                            className="upcoming-form-inputs"
                        ></DatePicker>
                    </Form.Item>

                    <Form.Item
                        label="Hotel Id"
                        name="hotel_id"
                        className="hidden"
                    >
                        <Input
                            id="hotel_id"
                            className="upcoming-form-inputs"
                            disabled
                        ></Input>
                    </Form.Item>
                    <Form.Item
                        wrapperCol={{
                            offset: 8,
                            span: 16,
                        }}
                    >
                        <Button type="primary" htmlType="submit">
                            Submit
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}

export { UpcomingHotelList };
